import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { environment } from '../../../environments/environment';
import { IntegrationForm, IntegrationsResponse } from '../models/integrations';
import { ODataService } from './odata.service';
import { map, debounceTime, distinctUntilChanged } from 'rxjs';
import { Subject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class IntegrationService {
  private http = inject(HttpClient);
  private can_be_multitenant = true;

  public testServiceNowCredentials(request: any) {
    const url = `${environment.apiUrl}/v1/ticketintegration/administrative/integrationdetails/test_connection/`;
    return this.http.post(url, request);
  }

  public addServiceNowCredentials(request: any) {
    const url = `${environment.apiUrl}/v1/ticketintegration/administrative/integrationdetails/`;
    return this.http.post(url, request);
  }

  public updateServiceNowCredentials(request: any) {
    const url = `${environment.apiUrl}/v1/ticketintegration/administrative/integrationdetails(${request.organization_id})/`;
    return this.http.put(url, request);
  }

  public getActiveIntegration(organizationId: number) {
    const url = `${environment.apiUrl}/v1/ticketintegration/administrative/integrationdetails(${organizationId})/`;
    return this.http.get(url);
  }

  public getActiveIntegrationFieldValues(organizationId: number) {
    const url = `${environment.apiUrl}/v1/ticketintegration/administrative/integrationfielretrievedvaluesmap(${organizationId})/`;
    return this.http.get(url);
  }

  public updateFieldMappings(request: any) {
    const url = `${environment.apiUrl}/v1/ticketintegration/administrative/integrationfieldvaluesmap(${request.pk})/`;
    return this.http.put(url, request);
  }

  public addFieldMappings(request: any) {
    const url = `${environment.apiUrl}/v1/ticketintegration/administrative/integrationfieldvaluesmap/`;
    return this.http.post(url, request);
  }

  public updateConnectWiseCkb(request: any) {
    const url = `${environment.apiUrl}/v1/organization/administrative/ckb(${request.organizationId})/update_connect_wise_ckb/`;
    return this.http.put(url, request);
  }

  public getIngestorIntegrations() {
    const url = `${environment.apiUrl}/api/v1/ingestor/integrations/injestorintegrations/`;
    return this.http.get<IngestorIntegration[]>(url);
  }

  public getFormSetup(integrationId: number) {
    const url = `${environment.apiUrl}/v1/integration/administrative/integration(${integrationId})/get_setup/`;
    return this.http.get<IntegrationForm>(url);
  }

  public getIntegrations() {
    const url = `${environment.apiUrl}/v1/integration/administrative/integration/`;
    return this.http.get<IntegrationsResponse>(url);
  }

  public getIntegrationsByOrganization(organizationId: number) {
    const url = `${environment.apiUrl}/v1/integration/administrative/integration_by_organization(${organizationId})/`;
    return this.http.get<IntegrationsResponse>(url);
  }
  public setCanBeMultitenant(canbemultitenant: boolean) {
    this.can_be_multitenant = canbemultitenant;
  }
  public getCanBeMultitenant() {
    return this.can_be_multitenant;
  }
}

export class AutocompleteField {
  id: number | null = null;
  name: string | null = null;
  constructor(id: number, name: string) {
    this.id = id;
    this.name = name;
  }
}

@Injectable({
  providedIn: 'root',
})
export class AutocompleteFieldService {
  private baseurl: string = '';

  // Use signals for reactive data
  dataList = signal<Array<{ name: string; id: number }>>([]);
  stringDataList = signal<string[]>([]);
  isLoading = signal<boolean>(false);

  selected: string = '';
  private currentFilter: string = '';
  private currentRequest: any = null;
  private filterSubject = new Subject<string>();

  constructor(private http: HttpClient) {
    this.http = http;

    // Set up debounced filter changes
    this.filterSubject.pipe(debounceTime(300), distinctUntilChanged()).subscribe((filter) => {
      this.performFetch(filter);
    });
  }

  public setBaserUrl(url: string) {
    this.baseurl = `${environment.apiUrl}/${url}`;
  }

  public valueChange(value: any): void {
    this.selected = value;
  }

  public filterChange(filter: any): void {
    this.currentFilter = filter;
    this.filterSubject.next(filter);
  }

  private performFetch(filter: string): void {
    // Cancel previous request if it exists
    if (this.currentRequest) {
      this.currentRequest.unsubscribe();
    }

    // Set loading state
    this.isLoading.set(true);

    let url = this.baseurl;
    if (filter && filter.length > 0) {
      url += `?q=${encodeURIComponent(filter)}`;
    }

    this.currentRequest = this.http.get<Array<{ name: string; id: number }>>(url).subscribe({
      next: (items) => {
        this.dataList.set(items || []);
        this.stringDataList.set((items || []).map((item) => item.name));
        this.isLoading.set(false);
      },
      error: (error) => {
        console.error('Error fetching autocomplete data:', error);
        this.dataList.set([]);
        this.stringDataList.set([]);
        this.isLoading.set(false);
      },
    });
  }

  public getCurrentFilter(): string {
    return this.currentFilter;
  }

  public clearData(): void {
    this.dataList.set([]);
    this.stringDataList.set([]);
    this.isLoading.set(false);
    this.currentFilter = '';

    if (this.currentRequest) {
      this.currentRequest.unsubscribe();
      this.currentRequest = null;
    }
  }

  public getSelectionByName(name: string): { name: string; id: number } | null {
    return this.dataList().find((selection) => selection.name === name) || null;
  }
}

export interface IngestorIntegration {
  id: number;
  name: string;
  icon: string;
}
